import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import { useCalendarEventStore } from '../../stores/root_store';
import { infoApi } from '../../api/info_api';
import type { InfoRecord } from '../../api/info_api';
import dayjs from 'dayjs';

interface CalendarCardProps {
  requestKey: string;
  canWork: boolean;
  clientUuid?: string;
}

export const CalendarCard = observer(function CalendarCard({
  requestKey,
  canWork,
  clientUuid,
}: CalendarCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const calendarEventStore = useCalendarEventStore();

  const [loading, setLoading] = useState(true);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [calendarPreferences, setCalendarPreferences] = useState('');
  const [createdEvents, setCreatedEvents] = useState<InfoRecord[]>([]);

  useEffect(() => {
    const checkCalendar = async () => {
      if (!requestKey || !clientUuid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await calendarEventStore.checkHasCalendar(
          'request',
          requestKey
        );
        setHasCalendar(result);
        if (result && calendarEventStore.hasCalendarInfo) {
          setCalendarPreferences(calendarEventStore.hasCalendarInfo.preferences || '');
        }
      } catch {
        setHasCalendar(false);
      } finally {
        setLoading(false);
      }
    };

    checkCalendar();
  }, [requestKey, clientUuid, calendarEventStore]);

  // Fetch created calendar events for this request
  // Refetch when location changes (e.g., returning from calendar page)
  useEffect(() => {
    const fetchCreatedEvents = async () => {
      if (!requestKey) return;
      try {
        const response = await infoApi.listByParent({
          parent_type: 'request',
          parent_uuid: requestKey,
          associated_type: 'calendar:event-create',
        });
        setCreatedEvents(response.items || []);
      } catch (err) {
        console.error('Error fetching created events:', err);
      }
    };
    fetchCreatedEvents();
  }, [requestKey, location.pathname]);

  const host = window.location.protocol + "//" + window.location.host;

  // Parse event data from Info record
  const parseEventData = (info: InfoRecord) => {
    try {
      const data = JSON.parse(info.data);
      return {
        summary: data.summary || 'Untitled Event',
        start: data.start?.dateTime || data.start?.date,
      };
    } catch {
      return null;
    }
  };

  const handleViewCalendar = () => {
    navigate(`/assistant/request/${requestKey}/calendar`);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarMonthIcon color="primary" />
          <Typography variant="h6">Calendar</Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        {!loading && !hasCalendar && (
          <>
            <Typography variant="body2" color="text.secondary">
              Client has not connected their calendar.
            </Typography>
            <Typography variant="body2" color="text.secondary" pt={1}>
              You can ask them to connect it by visiting: {host}/settings/calendar
            </Typography>
          </>
        )}

        {!loading && hasCalendar && (
          <>
            {calendarPreferences && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight="bold">
                  Scheduling notes:
                </Typography>
                <Typography variant="body2">{calendarPreferences}</Typography>
              </Alert>
            )}

            {createdEvents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Events Created
                </Typography>
                <List dense disablePadding>
                  {createdEvents.map((info) => {
                    const event = parseEventData(info);
                    if (!event) return null;
                    return (
                      <ListItem key={info.uuid} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <EventIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={event.summary}
                          secondary={event.start ? dayjs(event.start).format('MMM D, YYYY h:mm A') : ''}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              onClick={handleViewCalendar}
              disabled={!canWork}
              fullWidth
            >
              View Calendar
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
});
